import {
  PanelLeft,
  PanelRight,
  CalendarRange,
  SplitSquareHorizontal,
  ZoomIn,
  ZoomOut,
  LocateFixed,
  Camera,
  Maximize,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import type { DemoYear } from "../types/agricultural-lulc";

interface ExploreToolbarProps {
  layerPanelVisible: boolean;
  detailPanelVisible: boolean;
  comparisonMode: boolean;
  lulcVisible: boolean;
  year: DemoYear;
  onToggleLayerPanel: () => void;
  onToggleDetailPanel: () => void;
  onToggleComparison: () => void;
  onToggleYear: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onRecenter?: () => void;
  onScreenshot?: () => void;
  onFullscreen?: () => void;
}

export function ExploreToolbar({
  layerPanelVisible,
  detailPanelVisible,
  comparisonMode,
  lulcVisible,
  year,
  onToggleLayerPanel,
  onToggleDetailPanel,
  onToggleComparison,
  onToggleYear,
  onZoomIn,
  onZoomOut,
  onRecenter,
  onScreenshot,
  onFullscreen,
}: ExploreToolbarProps) {
  // The year toggle only drives the LULC layer (2024 / 2025 / change). If
  // LULC is hidden it has nothing to act on, and comparison pins it to
  // 2024 ↔ 2025 so the user can't change it from here.
  const yearToggleDisabled = comparisonMode || !lulcVisible;
  const yearToggleReason = comparisonMode
    ? "Disable comparison to change year"
    : !lulcVisible
      ? "Enable LULC to change year"
      : "Toggle year";
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1 rounded-lg border border-border bg-card/90 backdrop-blur shadow-lg px-1.5 py-1">
        {/* Left panel toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={layerPanelVisible ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={onToggleLayerPanel}
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {layerPanelVisible ? "Hide" : "Show"} layers
          </TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-5 mx-0.5" />

        {/* Zoom in */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onZoomIn}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Zoom in</TooltipContent>
        </Tooltip>

        {/* Zoom out */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onZoomOut}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Zoom out</TooltipContent>
        </Tooltip>

        {/* Recenter */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRecenter}
            >
              <LocateFixed className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Recenter</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-5 mx-0.5" />

        {/* Year toggle — disabled while comparison is on (the swipe is pinned
             to 2024 ↔ 2025) or while LULC is hidden (nothing to drive). */}
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrap the disabled button so the tooltip still triggers — Radix
                 Tooltip ignores pointer events on disabled buttons. */}
            <span tabIndex={0}>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs font-medium"
                onClick={onToggleYear}
                disabled={yearToggleDisabled}
              >
                <CalendarRange className="h-4 w-4 mr-1" />
                {year === "change" ? "Change" : year}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{yearToggleReason}</TooltipContent>
        </Tooltip>

        {/* Year comparison */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={comparisonMode ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={onToggleComparison}
            >
              <SplitSquareHorizontal className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Year comparison</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-5 mx-0.5" />

        {/* Screenshot */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onScreenshot}
            >
              <Camera className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Screenshot</TooltipContent>
        </Tooltip>

        {/* Fullscreen */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onFullscreen}
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Fullscreen</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-5 mx-0.5" />

        {/* Right panel toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={detailPanelVisible ? "secondary" : "ghost"}
              size="icon"
              className="h-8 w-8"
              onClick={onToggleDetailPanel}
            >
              <PanelRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {detailPanelVisible ? "Hide" : "Show"} details
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
